/**
 * Formats a Date object as a date-only string (YYYY-MM-DD) without timezone conversion.
 *
 * Use this for date-only fields like birthDate where the date should remain exactly
 * as the user selected it, regardless of timezone. This prevents the common issue
 * where dates slip backward when serialized to UTC.
 *
 * @param date - The Date object to format (can also be a string or null/undefined)
 * @returns The date formatted as YYYY-MM-DD string, or empty string if invalid
 *
 * @example
 * // User selects March 16, 1975 in any timezone
 * formatDateOnly(new Date(1975, 2, 16)) // Returns "1975-03-16"
 * // Backend returns ISO string
 * formatDateOnly("1975-03-16T00:00:00.000+00:00") // Returns "1975-03-16"
 */
export function formatDateOnly(date: Date | string | null | undefined): string {
    if (!date) {
        return '';
    }

    // If it's a string, extract the date portion directly to avoid timezone conversion
    if (typeof date === 'string') {
        // Handle ISO strings like '1975-03-16T00:00:00.000+00:00' or '1975-03-16T00:00:00.000Z'
        if (date.includes('T')) {
            return date.split('T')[0];
        }
        // Already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date;
        }
        // Fallback for other string formats - use Date parsing
        const d = new Date(date);
        if (isNaN(d.getTime())) {
            return '';
        }
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // For Date objects, use local date components (user's selected date from datepicker)
    if (isNaN(date.getTime())) {
        return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Parses a date value and returns a Date object for local display.
 *
 * For date-only fields stored as UTC (e.g., "1975-03-16T00:00:00.000Z"), this function
 * extracts the date portion and creates a Date in local timezone to prevent the date
 * from slipping backward in timezones west of UTC.
 *
 * @param dateValue - The date from the backend (can be string, Date, or null/undefined)
 * @returns A Date object for local display, or null if invalid
 *
 * @example
 * // Backend returns "1975-03-16T00:00:00.000Z"
 * // This function returns a Date that displays as March 16 regardless of timezone
 * parseDateForDisplay("1975-03-16T00:00:00.000Z")
 */
export function parseDateForDisplay(dateValue: string | Date | null | undefined): Date | null {
    if (!dateValue) {
        return null;
    }

    // For strings, extract date portion to avoid timezone conversion issues
    if (typeof dateValue === 'string') {
        let dateString = dateValue;
        if (dateValue.includes('T')) {
            dateString = dateValue.split('T')[0];
        }
        const [year, month, day] = dateString.split('-').map(Number);
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
            return null;
        }
        return new Date(year, month - 1, day);
    }

    // For Date objects, return as-is (already represents user's intended date)
    if (isNaN(dateValue.getTime())) {
        return null;
    }

    return dateValue;
}
